import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../../data/supabaseClient';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { CustomButton10Co, CustomButton10, CustomButton10Ca } from '../../Templates';
import Spinner from '../../common/Spinner';
import { FaMapMarkerAlt, FaCheckCircle, FaClock, FaUsers, FaLocationArrow } from 'react-icons/fa';

const PickupArrivalConfirmation = ({ 
  delivery, 
  currentUser, 
  otherUser, 
  userRole, 
  packageData,
  onUpdate, 
  addSystemMessage, 
  createNotification 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [showTimerDialog, setShowTimerDialog] = useState(false);
  const [isStartingTimer, setIsStartingTimer] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);

  // Determine user roles for pickup
  const isPackageSender = currentUser?.id === delivery?.sender_id;
  const isCarrier = currentUser?.id === delivery?.carrier_id;

  // Check arrival status - using "marked here" status like RideActionsV2
  const senderMarkedHere = !!delivery?.pickup_sender_here_at;
  const carrierMarkedHere = !!delivery?.pickup_carrier_here_at;
  const userHasMarked = isPackageSender ? senderMarkedHere : carrierMarkedHere;
  const otherHasMarked = isPackageSender ? carrierMarkedHere : senderMarkedHere;
  const bothMarked = senderMarkedHere && carrierMarkedHere;
  const timerStarted = !!delivery?.pickup_timer_started_at;

  // Get current location (with better accuracy like RideActionsV2)
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error(t('package_actions.geolocation_not_supported')));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          // Only accept if accuracy is within 50 meters
          if (accuracy > 50) {
            reject(new Error(t('package_actions.location_accuracy_low')));
            return;
          }

          try {
            // Reverse geocode to get address (optional)
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            
            resolve({
              latitude,
              longitude,
              accuracy,
              timestamp: new Date().toISOString(),
              address: data.display_name || t('package_actions.address_not_available')
            });
          } catch (error) {
            // If reverse geocoding fails, still return location
            resolve({
              latitude,
              longitude,
              accuracy,
              timestamp: new Date().toISOString(),
              address: t('package_actions.address_lookup_failed')
            });
          }
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  // Handle "I'm here" confirmation (marks user as present but doesn't start timer)
  const handleArrivalConfirmation = async (withLocation = false) => {
    setLoading(true);
    
    try {
      let locationProof = null;
      
      // Get location if requested
      if (withLocation) {
        setGettingLocation(true);
        try {
          locationProof = await getCurrentLocation();
        } catch (locError) {
          console.error('Location error:', locError);
          addToast({
            type: 'warning',
            title: t('package_actions.location_not_available'),
            description: locError.message || t('package_actions.location_not_available_desc')
          });
        }
        setGettingLocation(false);
      }

      const arrivalData = {
        timestamp: new Date().toISOString(),
        location_proof: locationProof,
        confirmed_by: currentUser.id
      };

      const updateField = isPackageSender ? 'pickup_sender_here_at' : 'pickup_carrier_here_at';
      
      const updateData = {
        [updateField]: arrivalData.timestamp,
        updated_at: new Date().toISOString()
      };

      // Add location proof if available
      if (locationProof) {
        const locationProofData = {
          [isPackageSender ? 'sender' : 'carrier']: arrivalData,
          timestamp: arrivalData.timestamp
        };
        updateData.pickup_location_proof = locationProofData;
      }

      // Just mark as here - don't change status or start timer yet
      // Check if both will be marked as here after this update
      const willBothBeHere = isPackageSender ? carrierMarkedHere : senderMarkedHere;
      
      // If both will be marked as here, change status to both_present
      if (willBothBeHere) {
        updateData.status = 'pickup_both_present';
      }

      // Update delivery
      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update(updateData)
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Update parent state immediately
      if (onUpdate && updatedDelivery) {
        onUpdate(updatedDelivery);
      }

      // Get other user ID for notification
      const otherUserId = isPackageSender ? delivery.carrier_id : delivery.sender_id;

      // Check if both will be marked as here
      // const willBothBeHere = isPackageSender ? carrierMarkedHere : senderMarkedHere; // REMOVED - already declared above

      if (willBothBeHere) {
        // Both are now marked as here
        await createNotification(
          otherUserId,
          'pickup_both_marked_here',
          t('package_actions.both_marked_here_notification'),
          delivery.id
        );

        await addSystemMessage(
          t('package_actions.both_marked_here_system') + 
          (locationProof ? ' ' + t('package_actions.location_shared') : '')
        );
      } else {
        // First person marked as here
        await createNotification(
          otherUserId,
          'pickup_marked_here',
          t('package_actions.marked_here_notification', { 
            role: otherUser?.username || userRole 
          }),
          delivery.id
        );

        await addSystemMessage(
          t('package_actions.marked_here_system', { 
            role: otherUser?.username || userRole 
          }) + (locationProof ? ' ' + t('package_actions.location_shared') : '')
        );
      }

      addToast({
        type: 'success',
        title: t('package_actions.marked_as_here'),
        description: willBothBeHere ? 
          t('package_actions.both_present_ready_to_start') : 
          t('package_actions.other_notified_here')
      });

      setShowLocationDialog(false);

    } catch (error) {
      console.error('Error confirming arrival:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_confirming_arrival')
      });
    } finally {
      setLoading(false);
      setGettingLocation(false);
    }
  };

  // Start waiting timer manually
  const handleStartTimer = async () => {
    setIsStartingTimer(true);
    
    try {
      const now = new Date().toISOString();
      
      const updateData = {
        pickup_timer_started_at: now,
        pickup_timer_manually_started: true,
        status: 'pickup_arrival_phase',
        updated_at: now
      };

      // Also mark as arrived if starting timer
      if (isPackageSender && !delivery.pickup_sender_here_at) {
        updateData.pickup_sender_here_at = now;
      } else if (!isPackageSender && !delivery.pickup_carrier_here_at) {
        updateData.pickup_carrier_here_at = now;
      }
      
      // If both users marked here, mark both as arrived and change status to waiting
      if (otherHasMarked) {
        updateData.status = 'pickup_waiting';
        // Mark other as arrived too
        if (isPackageSender && !delivery.pickup_carrier_here_at) {
          updateData.pickup_carrier_here_at = now;
        } else if (!isPackageSender && !delivery.pickup_sender_here_at) {
          updateData.pickup_sender_here_at = now;
        }
      }

      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update(updateData)
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Update parent state immediately
      if (onUpdate && updatedDelivery) {
        onUpdate(updatedDelivery);
      }

      // Get other user ID for notification
      const otherUserId = isPackageSender ? delivery.carrier_id : delivery.sender_id;

      await createNotification(
      otherUserId,
      'pickup_timer_started',
      t('package_actions.pickup_timer_started_notification', { 
      role: otherUser?.username || userRole, 
      minutes: delivery.meeting_duration_minutes || 3 
      }),
      delivery.id
      );

      await addSystemMessage(
      t('package_actions.pickup_timer_started_system', { 
      role: otherUser?.username || userRole, 
      minutes: delivery.meeting_duration_minutes || 3 
      })
      );

      addToast({
        type: 'info',
        title: t('package_actions.timer_started'),
        description: t('package_actions.waiting_period_active')
      });

      setShowTimerDialog(false);

    } catch (error) {
      console.error('Error starting timer:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_starting_timer')
      });
    } finally {
      setIsStartingTimer(false);
    }
  };

  // Check if meeting time has arrived
  const meetingTimeArrived = () => {
    if (!delivery?.pickup_meeting_time) return false;
    return new Date() >= new Date(delivery.pickup_meeting_time);
  };

  // Render pickup location info
  const renderLocationInfo = () => (
    <div className="PAC-location-section">
      <div className="PAC-location-title">
        <FaMapMarkerAlt size={12} />
        {t('package_actions.pickup_location')}
      </div>
      <div className="PAC-location-address">
        {packageData?.address_from_street}, {packageData?.address_from_city}
        {packageData?.address_from_country && `, ${packageData.address_from_country}`}
      </div>
    </div>
  );

  // Render meeting time info - REMOVED per user request
  // This information is now displayed in the header
  // const renderMeetingTimeInfo = () => {
  //   if (!delivery?.pickup_meeting_time) return null;
  //   
  //   const meetingTime = new Date(delivery.pickup_meeting_time);
  //   const isTimeToMeet = meetingTimeArrived();
  //   
  //   return (
  //     <div className="PAC-meeting-time-display">
  //       <FaClock size={12} style={{ marginRight: '6px' }} />
  //       {t('package_actions.pickup_time')}: {meetingTime.toLocaleString('en-GB', {
  //         day: '2-digit',
  //         month: '2-digit',
  //         year: 'numeric',
  //         hour: '2-digit',
  //         minute: '2-digit',
  //         hour12: false
  //       })}
  //       {!isTimeToMeet && (
  //         <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '2px' }}>
  //           {t('package_actions.arrives_in')} {Math.ceil((meetingTime - new Date()) / (1000 * 60))} {t('package_actions.minutes')}
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  // Render presence indicators - now shows "marked here" status
  const renderPresenceIndicators = () => (
    <div className="PAC-presence-indicators">
      <div className={`PAC-presence-indicator ${senderMarkedHere ? 'present' : ''}`}>
        <div className="PAC-presence-icon">
          {senderMarkedHere ? <FaCheckCircle /> : <FaClock />}
        </div>
        <div className="PAC-presence-label">
          {t('package_actions.sender')}
        </div>
      </div>
      
      <div className={`PAC-presence-indicator ${carrierMarkedHere ? 'present' : ''}`}>
        <div className="PAC-presence-icon">
          {carrierMarkedHere ? <FaCheckCircle /> : <FaClock />}
        </div>
        <div className="PAC-presence-label">
          {t('package_actions.carrier')}
        </div>
      </div>
    </div>
  );

  return (
    <div className="PAC-meeting-section">
      
      {renderLocationInfo()}
      {/* renderMeetingTimeInfo() - REMOVED per user request */}
      {renderPresenceIndicators()}
      
      {timerStarted ? (
        // Timer already started, waiting phase will handle this
        <div className="PAC-timer-already-started">
          <FaClock size={16} />
          <span>{t('package_actions.timer_already_running')}</span>
        </div>
      ) : (
        <div className="PAC-arrival-actions">
          {!userHasMarked && (
            <div className="PAC-arrival-step">
              <div className="PAC-arrival-info">
                <p>{t('package_actions.let_other_know_here')}</p>
              </div>
              <CustomButton10
                onClick={() => setShowLocationDialog(true)}
                label={t('package_actions.im_here')}
                disabled={loading}
              >
                <FaLocationArrow />
              </CustomButton10>
            </div>
          )}
          
          {/* Show Start Timer button only when user is here but other isn't */}
          {userHasMarked && !otherHasMarked && (
            <div className="PAC-timer-step">
              <div className="PAC-timer-info">
                {delivery.pickup_meeting_time && (
                  <p className="PAC-meeting-time-reminder">
                    {t('package_actions.meeting_time_is', { 
                      time: new Date(delivery.pickup_meeting_time).toLocaleTimeString('en-GB', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: false 
                      }) 
                    })}
                  </p>
                )}
                <p>{t('package_actions.start_timer_when_ready')}</p>
              </div>
              <CustomButton10Co
                onClick={() => setShowTimerDialog(true)}
                label={t('package_actions.start_timer')}
                disabled={isStartingTimer}
              >
                {isStartingTimer ? <Spinner size="small" showText={false} /> : <FaClock />}
              </CustomButton10Co>
            </div>
          )}
          
          {/* Show waiting message to the user who hasn't marked here yet */}
          {otherHasMarked && !userHasMarked && (
            <div className="PAC-waiting-for-arrival">
              <p>{t('package_actions.other_user_waiting', { 
                role: otherUser?.username || (isPackageSender ? t('package_actions.carrier') : t('package_actions.sender')) 
              })}</p>
            </div>
          )}
        </div>
      )}
      
      {showLocationDialog && (
        <LocationConfirmDialog
          onCancel={() => setShowLocationDialog(false)}
          onConfirm={(shareLocation) => handleArrivalConfirmation(shareLocation)}
          isConfirming={loading}
          gettingLocation={gettingLocation}
          userRole={userRole}
        />
      )}
      
      {showTimerDialog && (
        <TimerStartDialog
          onCancel={() => setShowTimerDialog(false)}
          onConfirm={handleStartTimer}
          isStarting={isStartingTimer}
          userRole={userRole}
          waitMinutes={delivery.meeting_duration_minutes || 3}
        />
      )}
    </div>
  );
};

// Location Confirmation Dialog Component - Renders as fullscreen modal
const LocationConfirmDialog = ({
  onCancel,
  onConfirm,
  isConfirming,
  gettingLocation,
  userRole
}) => {
  const { t } = useTranslation('common');
  const [shareLocation, setShareLocation] = useState(false);

  return ReactDOM.createPortal(
    <div 
      className="PAC-modal-overlay-v2" 
      onClick={(e) => {
        // Only close if clicking directly on overlay
        if (e.target === e.currentTarget && !isConfirming) {
          onCancel();
        }
      }}
    >
      <div className="PAC-modal2-v2">
        <div className="PAC-location-dialog">
          <div className="PAC-dialog-content">
            <h4>{t('package_actions.confirm_your_arrival')}</h4>
            <p>{t('package_actions.let_role_know_arrived', { 
              role: userRole === t('package_actions.sender') ? t('package_actions.carrier') : t('package_actions.sender')
            })}</p>
            
            <div className="PAC-location-option">
              <label className="PAC-checkbox-label">
                <input
                  type="checkbox"
                  checked={shareLocation}
                  onChange={(e) => setShareLocation(e.target.checked)}
                  disabled={isConfirming}
                />
                <span>{t('package_actions.share_location_proof')}</span>
              </label>
              <p className="PAC-location-help">
                {t('package_actions.optional_gps_proof')}
              </p>
            </div>
            
            <div className="PAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label={t('buttons.cancel')}
                disabled={isConfirming}
              />
              <CustomButton10Co
                onClick={() => onConfirm(shareLocation)}
                disabled={isConfirming || gettingLocation}
              >
                {isConfirming || gettingLocation ? (
                  <Spinner size="small" showText={false} />
                ) : (
                  t('package_actions.im_here')
                )}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Timer Start Dialog Component
const TimerStartDialog = ({
  onCancel,
  onConfirm,
  isStarting,
  userRole,
  waitMinutes
}) => {
  const { t } = useTranslation('common');

  return ReactDOM.createPortal(
    <div 
      className="PAC-modal-overlay-v2" 
      onClick={(e) => {
        if (e.target === e.currentTarget && !isStarting) {
          onCancel();
        }
      }}
    >
      <div className="PAC-modal2-v2">
        <div className="PAC-timer-start-dialog">
          <div className="PAC-dialog-content">
            <h4>{t('package_actions.start_waiting_timer')}</h4>
            <p>{t('package_actions.timer_will_run_for', { minutes: waitMinutes })}</p>
            <p className="PAC-timer-notice">
              {t('package_actions.other_user_will_be_notified')}
            </p>
            
            <div className="PAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label={t('buttons.cancel')}
                disabled={isStarting}
              />
              <CustomButton10Co
                onClick={onConfirm}
                disabled={isStarting}
              >
                {isStarting ? (
                  <Spinner size="small" showText={false} />
                ) : (
                  t('package_actions.start_timer')
                )}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PickupArrivalConfirmation;